import { useEffect, useRef } from "react"

export function useInterval(callback: Function, timeout: number, updateOn: any[] = []) {
  useEffect(() => {
    const interval = setInterval(callback, timeout)
    return () => {
      clearInterval(interval)
    }
  }, updateOn)
}

/**
 * Like useInterval, but skips ticks while the tab is hidden and catches up with a single
 * call when it becomes visible again. Use for data-refresh polling only — not for lock
 * refreshes or autosaves, which must keep running in background tabs.
 */
export function usePollingInterval(callback: () => void, timeout: number) {
  const ref = useRef(callback)
  ref.current = callback

  useEffect(() => {
    let missedTick = false
    let interval: ReturnType<typeof setInterval>

    function tick() {
      if (document.hidden) {
        missedTick = true
        return
      }
      ref.current()
    }

    function start() {
      interval = setInterval(tick, timeout)
    }

    function handleVisibilityChange() {
      if (document.hidden || !missedTick) {
        return
      }
      missedTick = false
      clearInterval(interval)
      ref.current()
      start()
    }

    start()
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [timeout])
}

export function useAsyncInterval(callback: () => Promise<any>, ms: number) {
  const ref = useRef(callback)
  ref.current = callback

  useEffect(() => {
    let cancelled = false

    async function run() {
      while (!cancelled) {
        await Promise.all([
          ref.current().catch(console.error),
          new Promise(res => setTimeout(res, ms)),
        ])
      }
    }
    run()

    return () => {
      cancelled = true
    }
  }, [ms])
}
